import { useCallback, useState } from "react";

/**
 * 배너 닫기 상태를 만료와 함께 보관한다.
 *
 * 기존 구현은 `localStorage` 에 영구 기록해서 한 번 닫으면 다시는 노출되지 않았다.
 * 설치·알림 안내는 사용자가 나중에 마음을 바꿀 수 있는 성격이라 만료를 둔다.
 */
export function useDismissible(
  key: string,
  ttlDays = 14
): { dismissed: boolean; dismiss: () => void; reset: () => void } {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    const raw = window.localStorage.getItem(key);
    if (!raw) return false;
    const until = Number(raw);
    // 값이 숫자가 아니면 만료 없는 구버전 기록 → 만료된 것으로 보고 재노출한다.
    if (!Number.isFinite(until)) {
      window.localStorage.removeItem(key);
      return false;
    }
    if (Date.now() >= until) {
      window.localStorage.removeItem(key);
      return false;
    }
    return true;
  });

  const dismiss = useCallback(() => {
    window.localStorage.setItem(key, String(Date.now() + ttlDays * 24 * 60 * 60 * 1000));
    setDismissed(true);
  }, [key, ttlDays]);

  const reset = useCallback(() => {
    window.localStorage.removeItem(key);
    setDismissed(false);
  }, [key]);

  return { dismissed, dismiss, reset };
}
